import React, { useState, useEffect, useMemo } from "react";
import { formatInstant, formatUtc, addUtc, zonedDateBoundary } from "../../../../utilities/dateTime";
import {
  Calendar, Clock, MapPin, Info, X, CheckCircle2, Type, AlertCircle
} from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "../../../../firebase/AuthProvider";
import { CalendarDirectoryPerson } from "../../../../interfaces/calendar/calendarDirectoryOptions";
import Team from "../../../../interfaces/identity/Team";
import { createEvent, getCalendarAttendeeOptions } from "../../../../services/calendarService/EventService";
import { getTaskById } from "../../../../services/taskServices/TaskService";
import ParticipantSelectDropdown from "../../../ui/Dropdown/ParticipantSelectDropdown";
import { normalizeInviteeId } from "../../../ui/Dropdown/participantInviteSelection";
import CustomInput from "../CustomInput";
import TimePicker from "../TimePicker";
import AsyncTaskDropdown, { CalendarTaskSelection } from "../AsyncTaskDropdown";
import { mapToUnifiedAttendees } from "../../../../utilities/calendar/calendarMapper";
import "./EventStyles.css";
import { getDisplayErrorMessage } from "../../../../utilities/ErrorMessages/getErrorMessage";
import RecurrenceSelector from "./RecurrenceSelector";
import ToggleSwitch from "../../../ui/Forms/ToggleSwitch";
import { wallTimeInstant, dateCarrier, wallTimePreview } from "../../../../utilities/calendar/calendarDisplayTime";

interface AddEventProps {
  // Kept for compatibility with existing AddEvent callers. AddEvent is a create
  // form, so the value is not currently consumed, but Navbar/AddNewBtn already
  // passes event={null}.
  event?: any;
  meetingTask?: any;
  onClose?: () => void;
  onEventCreated?: () => void;
  // Existing callers may hold an explicitly nullable selected-slot instant.
  clickedDateTime?: Date | null;
  planningMode?: boolean;
  displayTimeZone?: string;
  initialAllDay?: boolean;
}

export default function AddEvent({
  meetingTask,
  onClose,
  onEventCreated,
  clickedDateTime,
  planningMode = false,
  displayTimeZone,
  initialAllDay = false,
}: AddEventProps) {
  const { currentUser } = useAuth();
  const userId = (currentUser as any)?.uid;
  const token = (currentUser as any)?.accessToken;
  const excludedInviteeUserIds = useMemo(
    () => userId ? [String(userId)] : [],
    [userId]
  );

  // In planning mode, the dropped/clicked task is the authoritative task link.
  // Managers may schedule their own focus time for tasks assigned to employees
  // they supervise, so this must not depend on /userassignedtask returning it.
  const planningTaskId = useMemo(
    () =>
      planningMode && meetingTask?.id != null
        ? Number(meetingTask.id)
        : null,
    [planningMode, meetingTask?.id]
  );

  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState<CalendarDirectoryPerson[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTask, setSelectedTask] = useState<CalendarTaskSelection | null>(
    meetingTask ?? null
  );
  const [isLinkedTaskLoading, setIsLinkedTaskLoading] = useState(
    Boolean(meetingTask?.id)
  );
  const [linkedTaskLoadFailed, setLinkedTaskLoadFailed] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const currentTimeZone = displayTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const MAX_NOTES_LENGTH = 1000;
  const [isAllDay, setIsAllDay] = useState(Boolean(displayTimeZone && initialAllDay));

  // Keep keyboard behavior consistent with the rest of the calendar modals.
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const [selectedDate, setSelectedDate] = useState<string>(
    formatInstant(clickedDateTime ?? new Date(), "yyyy-MM-dd", currentTimeZone)
  );

  const [formData, setFormData] = useState(() => {
    const now = new Date();
    const isMidnight = clickedDateTime && formatInstant(clickedDateTime, "HH:mm", currentTimeZone) === "00:00";
    const initialStart = (clickedDateTime && !isMidnight) ? new Date(clickedDateTime) : now;
    const initialEnd = addUtc(initialStart, planningMode ? 60 : 30, "minutes");

    const taskReference = meetingTask
      ? `${meetingTask.organization?.abbreviation ? `${meetingTask.organization.abbreviation}-` : ""}${meetingTask.friendlyId ?? meetingTask.id}`
      : "";

    return {
      title: planningMode && meetingTask
        ? `Work on ${taskReference}${meetingTask.title ? ` — ${meetingTask.title}` : ""}`
        : "",
      startTime: formatInstant(initialStart, "h:mm a", currentTimeZone),
      endTime: formatInstant(initialEnd, "h:mm a", currentTimeZone),
      description: "",
      location: "Waivera",
      participantUserIds: [] as string[],
      individualUserIds: [] as string[],
      selectedTeamIds: [] as number[],
      externalUserEmails: [] as string[],
      taskId: meetingTask?.id ? Number(meetingTask.id) : 0,
      isSprout: false,
      recurrenceRule: undefined as string | undefined,
    };
  });

  const getTaskParticipantId = (
    task?: CalendarTaskSelection | null
  ): string | null => {
    const participantId = task?.participant?.id;
    return participantId == null ? null : String(participantId).toLowerCase();
  };

  const getTaskParticipantName = (
    task?: CalendarTaskSelection | null
  ): string => {
    const participant = task?.participant;
    if (!participant) return "the task participant";

    const name = `${participant.firstName ?? ""} ${participant.lastName ?? ""}`.trim();
    return name || "the task participant";
  };

  // Load the calendar directory and, when AddEvent was launched with a task
  // reference, hydrate that one task directly. This avoids waiting for a large
  // task collection before attendee restrictions become authoritative.
  useEffect(() => {
    if (!token) return;

    const init = async () => {
      try {
        const employeePromise = getCalendarAttendeeOptions(token, "create");
        const taskPromise = meetingTask?.id
          ? getTaskById(String(meetingTask.id), token)
          : Promise.resolve(null);

        const [emp, hydratedTask] = await Promise.all([
          employeePromise,
          taskPromise,
        ]);

        setEmployees(emp.people);
        setTeams(emp.teams);

        if (meetingTask?.id) {
          if (hydratedTask) {
            setSelectedTask(hydratedTask);
            setLinkedTaskLoadFailed(false);
          } else {
            setSelectedTask(meetingTask);
            setLinkedTaskLoadFailed(true);
          }
        }
      } catch (error) {
        console.error("Failed to load AddEvent initial data", error);
        if (meetingTask?.id) {
          setSelectedTask(meetingTask);
          setLinkedTaskLoadFailed(true);
        }
      } finally {
        setIsLinkedTaskLoading(false);
      }
    };

    init();
  }, [meetingTask, token]);

  // 2. Time Logic Validation (end after start + no past date/time)
  useEffect(() => {
    try {
      if (isAllDay) {
        if (selectedDate < formatInstant(new Date(), 'yyyy-MM-dd', currentTimeZone)) throw new Error('Meeting date cannot be in the past');
        setErrors(prev => { const next = { ...prev }; delete next.time; return next; });
        return;
      }
      const start = wallTimeInstant(selectedDate, formData.startTime, currentTimeZone);
      const end = wallTimeInstant(selectedDate, formData.endTime, currentTimeZone);
      const now = new Date();
      const today = formatInstant(now, "yyyy-MM-dd", currentTimeZone);

      if (selectedDate < today) {
        setErrors(prev => ({ ...prev, time: "Meeting date cannot be in the past" }));
        return;
      }

      if (Math.floor(+end / 60000) <= Math.floor(+start / 60000)) {
        setErrors(prev => ({ ...prev, time: "End time must be after start time" }));
        return;
      }

      // Compare by minute to avoid marking the current minute as "past" due to seconds precision
      if (Math.floor(+start / 60000) < Math.floor(+now / 60000)) {
        setErrors(prev => ({ ...prev, time: "Start time cannot be in the past" }));
        return;
      }

      if (Math.floor(+end / 60000) < Math.floor(+now / 60000)) {
        setErrors(prev => ({ ...prev, time: "End time cannot be in the past" }));
        return;
      }

      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.time;
        return newErrors;
      });
    } catch (err) {
      setErrors(prev => ({ ...prev, time: err instanceof Error ? err.message : "Choose a valid calendar time." }));
    }
  }, [formData.startTime, formData.endTime, selectedDate, currentTimeZone, isAllDay]);

  const handleStartTimeChange = (newStartTime: string) => {
    const start = wallTimeInstant("2000-01-01", newStartTime, "UTC");
    const newEndTime = formatUtc(addUtc(start, planningMode ? 60 : 30, "minutes"), "h:mm a");
    setFormData(prev => ({ ...prev, startTime: newStartTime, endTime: newEndTime }));
  };

  // --- TWO-WAY CONSTRAINT LOGIC ---

  // A. Match the exact logic used in ParticipantSelectDropdown to identify non-employees
  const allWaiverParticipantIds = useMemo(() => {
    return employees.filter(emp => {
      const isEmployee = emp.type?.toLowerCase() === 'employee' ||
        (emp as any).userType?.toLowerCase() === 'employee' ||
        !emp.type;
      return !isEmployee; // Anyone not strictly an employee is a Waiver Participant
    }).map(emp => String(emp.userId).toLowerCase());
  }, [employees]);

  // B. Identify if a Waiver Participant is currently selected in the form
  const activeWaiverParticipantId = useMemo(() => {
    return formData.participantUserIds.find(id =>
      allWaiverParticipantIds.includes(String(id).toLowerCase())
    );
  }, [formData.participantUserIds, allWaiverParticipantIds]);

  // A selected task immediately restricts Waiver Participant invite choices
  // using the authoritative single-task hydration or an async search result.
  const availableEmployeesToInvite = useMemo(() => {
    if (!formData.taskId) return employees;

    if (isLinkedTaskLoading || !selectedTask) {
      return [];
    }

    const lockedWaiverParticipantId = getTaskParticipantId(selectedTask);
    if (!lockedWaiverParticipantId) return employees;

    return employees.filter((emp) => {
      const empIdLower = String(emp.userId).toLowerCase();
      const isWaiverParticipant = allWaiverParticipantIds.includes(empIdLower);

      return !isWaiverParticipant || empIdLower === lockedWaiverParticipantId;
    });
  }, [
    employees,
    formData.taskId,
    selectedTask,
    isLinkedTaskLoading,
    allWaiverParticipantIds,
  ]);

  const lockedTaskOwnerName = useMemo(
    () => getTaskParticipantName(selectedTask),
    [selectedTask]
  );

  const activeParticipantName = useMemo(() => {
    return employees.find(
      (employee) =>
        String(employee.userId).toLowerCase() ===
        String(activeWaiverParticipantId).toLowerCase()
    )?.userName || "the selected participant";
  }, [activeWaiverParticipantId, employees]);

  // Reassert the planning task after asynchronous option loads. Existing
  // AddEvent task lists are advisory in planning mode; they are not allowed to
  // clear or replace the task relationship established by the drag/click action.
  useEffect(() => {
    if (!planningTaskId || !Number.isFinite(planningTaskId)) return;

    setFormData((previous) =>
      previous.taskId === planningTaskId
        ? previous
        : { ...previous, taskId: planningTaskId }
    );
  }, [planningTaskId]);

  // --- SUBMIT LOGIC ---

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let validationErrors: Record<string, string> = {};
    if (!formData.title.trim()) validationErrors.title = "Title is required";

    if (Object.keys(validationErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...validationErrors }));
      toast.error("Please fix errors before scheduling");
      return;
    }

    if (errors.time) {
      toast.error("Please fix errors before scheduling");
      return;
    }

    let startUtc: string, endUtc: string;
    try {
      dateCarrier(selectedDate);
      startUtc = isAllDay ? zonedDateBoundary(selectedDate, currentTimeZone).toISOString() : wallTimeInstant(selectedDate, formData.startTime, currentTimeZone).toISOString();
      endUtc = isAllDay ? zonedDateBoundary(formatUtc(addUtc(dateCarrier(selectedDate), 1), 'yyyy-MM-dd'), currentTimeZone).toISOString() : wallTimeInstant(selectedDate, formData.endTime, currentTimeZone).toISOString();
      if (endUtc <= startUtc) throw new Error('End time must be after start time');
    } catch (error) { setErrors(prev => ({ ...prev, time: error instanceof Error ? error.message : 'Choose a valid calendar time.' })); return; }
    setLoading(true);

    const eventDataToSend = {
      title: formData.title,
      description: formData.description,
      location: formData.isSprout ? formData.location : "Waivera",
      meetingDetails: "",
      ownerUserID: userId,
      // Planning mode always submits the task that initiated the planning
      // action, even if that task is not present in the normal task dropdown.
      taskId:
        planningTaskId && Number.isFinite(planningTaskId)
          ? planningTaskId
          : formData.taskId || null,
      startTime: startUtc,
      endTime: endUtc,
      ...(isAllDay ? { isAllDay: true, allDayStart: selectedDate, allDayEndExclusive: formatUtc(addUtc(dateCarrier(selectedDate), 1), 'yyyy-MM-dd') } : {}),
      time_zone: currentTimeZone,
      recurrenceRule: formData.recurrenceRule,
      online: true,
      isSprout: formData.isSprout,
      attendees: mapToUnifiedAttendees(
        formData.participantUserIds.filter(
          (inviteeId) => normalizeInviteeId(inviteeId) !== normalizeInviteeId(userId ?? "")
        ),
        formData.externalUserEmails
      )
    };

    try {
      const result = await createEvent(eventDataToSend as any, token);
      if (result.success) {
        toast.success("Event created");
        onEventCreated?.();
        onClose?.();
      } else {
        toast.error(result.message || "Failed to create event");
      }
    } catch (err: any) {
        if (err.name === 'AbortError') return;
        const msg = getDisplayErrorMessage(err, "Error to Create the Event");
        toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="calendar-event-modal dark-popup bg-white rounded-3xl shadow-2xl border border-gray-100 max-w-[600px] mx-auto animate-in fade-in zoom-in-95 duration-300">
      <div className="rounded-t-3xl px-8 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
        <div className="flex items-center gap-3">
          <div className="calendar-header-icon w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center shrink-0">
            <Calendar className="text-[#7E57C2]" size={20} />
          </div>

          <div className="flex flex-col justify-center text-left">
            <h2 className="text-xl m-0 font-bold text-gray-900 leading-tight">
              {planningMode ? "Schedule Task Work" : "Schedule Event"}
            </h2>
            <p className="text-[11px] text-[#7E57C2] font-bold uppercase tracking-[0.15em] m-0">
              {formatUtc(dateCarrier(selectedDate), "EEEE, MMMM dd, yyyy")} | {currentTimeZone}
            </p>
          </div>
        </div>

        <button 
          onClick={onClose} 
          className="p-2 hover:bg-white rounded-xl text-gray-400 hover:text-[#7E57C2] shadow-sm transition-all duration-200 border border-transparent hover:border-gray-200"
        >
          <X size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-8 space-y-8">
        {!planningMode && (
          <div className="calendar-meeting-toggle flex bg-gray-100/80 p-1.5 rounded-xl w-fit mx-auto border border-gray-200/50">
            <button type="button" onClick={() => setFormData({ ...formData, isSprout: false, location: "Waivera" })}
              aria-pressed={!formData.isSprout}
              className={`calendar-meeting-option px-8 py-2 rounded-lg text-sm font-bold transition-all duration-300 ${!formData.isSprout ? 'calendar-meeting-option-active bg-white text-[#7E57C2] shadow-sm border border-gray-200/50' : 'text-gray-500 hover:text-gray-700'}`}>
              Waivera Meeting
            </button>
            <button type="button" onClick={() => setFormData({ ...formData, isSprout: true, location: "" })}
              aria-pressed={formData.isSprout}
              className={`calendar-meeting-option px-8 py-2 rounded-lg text-sm font-bold transition-all duration-300 ${formData.isSprout ? 'calendar-meeting-option-active bg-white text-[#7E57C2] shadow-sm border border-gray-200/50' : 'text-gray-500 hover:text-gray-700'}`}>
              External Meeting
            </button>
          </div>
        )}

        {planningMode && meetingTask && (
          <div className="calendar-linked-task rounded-2xl border border-purple-100 bg-purple-50/60 px-4 py-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#7E57C2]">
              Linked Task
            </div>
            <div className="mt-1 text-sm font-bold text-gray-900">
              {meetingTask.organization?.abbreviation
                ? `${meetingTask.organization.abbreviation}-${meetingTask.friendlyId ?? meetingTask.id}`
                : `Task ${meetingTask.friendlyId ?? meetingTask.id}`}
            </div>
            <div className="mt-0.5 truncate text-xs font-medium text-gray-600" title={meetingTask.title}>
              {meetingTask.title}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-7">
          <div className="md:col-span-2">
            <CustomInput
              icon={Type}
              name="title"
              label="Event Title"
              placeholder="What is this meeting about?"
              value={formData.title}
              onChange={(e) => {
                setFormData({ ...formData, title: e.target.value });
                if (errors.title) setErrors(prev => { const n = { ...prev }; delete n.title; return n; });
              }}
              error={errors.title}
            />
          </div>

          {!planningMode && (
            <div className="md:col-span-2">
              <CustomInput
                icon={MapPin}
                name="location"
                label="Location"
                placeholder={formData.isSprout ? "Enter link or address" : "Waivera"}
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                disabled={!formData.isSprout}
              />
            </div>
          )}

          {displayTimeZone && !planningMode && <div className="md:col-span-2"><ToggleSwitch id="calendar-all-day" label="All day" checked={isAllDay} onChange={value => { setIsAllDay(value); if (value) setFormData(prev => ({ ...prev, recurrenceRule: undefined })); }} /></div>}
          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 items-start">
            <CustomInput icon={Calendar} type="date" name="date" label={planningMode ? "Work Date" : "Meeting Date"} value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
            {!isAllDay && <div className="flex flex-col gap-1">
              <TimePicker icon={Clock} label="Start Time" value={formData.startTime} onChange={(v) => handleStartTimeChange(v as string)} />
            </div>}
            {!isAllDay && <div className="flex flex-col gap-1">
              <TimePicker icon={Clock} label="End Time" value={formData.endTime} onChange={(v) => setFormData({ ...formData, endTime: v as string })} />
            </div>}
          </div>
          
          {!planningMode && !isAllDay && (
            <div className="md:col-span-2">
              <RecurrenceSelector
                value={formData.recurrenceRule}
                onChange={(rule) => setFormData(prev => ({ ...prev, recurrenceRule: rule }))}
                startTime={wallTimePreview(selectedDate, formData.startTime, currentTimeZone)}
                timeZone={currentTimeZone}
              />
            </div>
          )}

          {errors.time && (
            <div className="md:col-span-2 -mt-4 flex items-center gap-2 text-red-500 text-xs font-bold animate-in slide-in-from-top-1">
              <AlertCircle size={14} /> {errors.time}
            </div>
          )}

          {!planningMode && (
            <>
                        <div className="md:col-span-2">
                          {formData.taskId > 0 && isLinkedTaskLoading ? (
                            <div className="flex h-12 items-center rounded-xl border border-gray-200 bg-gray-50 px-4 text-sm font-medium text-gray-500">
                              Loading eligible people for the linked task…
                            </div>
                          ) : formData.taskId > 0 && linkedTaskLoadFailed && !selectedTask ? (
                            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-medium text-amber-800">
                              Linked task details could not be loaded. Invite changes are disabled until the task can be resolved.
                            </div>
                          ) : (
                            <ParticipantSelectDropdown
                              name="participants"
                              label="Invite People"
                              participantUserIds={formData.participantUserIds}
                              individualUserIds={formData.individualUserIds}
                              selectedTeamIds={formData.selectedTeamIds}
                              externalUserEmails={formData.externalUserEmails}
                              options={availableEmployeesToInvite}
                              teams={teams}
                              excludedUserIds={excludedInviteeUserIds}
                              onChange={(data) => {
                                const selectedWaivers = data.participantUserIds.filter((id) =>
                                  allWaiverParticipantIds.includes(String(id).toLowerCase())
                                );
                                const newActiveWaiverId =
                                  selectedWaivers.length > 0 ? selectedWaivers[0] : null;

                                if (
                                  formData.taskId &&
                                  selectedTask &&
                                  newActiveWaiverId
                                ) {
                                  const taskParticipantId =
                                    getTaskParticipantId(selectedTask);

                                  if (
                                    taskParticipantId &&
                                    taskParticipantId !==
                                      String(newActiveWaiverId).toLowerCase()
                                  ) {
                                    setSelectedTask(null);
                                    setFormData((previous) => ({
                                      ...previous,
                                      ...data,
                                      taskId: 0,
                                    }));
                                    return;
                                  }
                                }

                                setFormData((previous) => ({
                                  ...previous,
                                  ...data,
                                }));
                              }}
                            />
                          )}

                          {formData.taskId > 0 && selectedTask && !isLinkedTaskLoading && (
                            <div className="mt-2 ml-1 flex items-center gap-1.5 text-xs font-semibold text-[#7E57C2]">
                              <Info size={14} />
                              List restricted to staff and {lockedTaskOwnerName} due to selected task.
                            </div>
                          )}
                        </div>

                        <div className="md:col-span-2">
                          <AsyncTaskDropdown
                            authToken={token}
                            selectedTask={selectedTask}
                            participantId={activeWaiverParticipantId || null}
                            onChange={(task) => {
                              setSelectedTask(task);
                              setLinkedTaskLoadFailed(false);

                              const newTaskId = task ? Number(task.id) : 0;

                              setFormData((previous) => {
                                let participantUserIds = [
                                  ...previous.participantUserIds,
                                ];

                                if (task) {
                                  const taskParticipantId =
                                    getTaskParticipantId(task);

                                  if (taskParticipantId) {
                                    participantUserIds =
                                      participantUserIds.filter((id) => {
                                        const normalizedId =
                                          String(id).toLowerCase();
                                        const isWaiverParticipant =
                                          allWaiverParticipantIds.includes(
                                            normalizedId
                                          );

                                        return (
                                          !isWaiverParticipant ||
                                          normalizedId === taskParticipantId
                                        );
                                      });
                                  }
                                }

                                return {
                                  ...previous,
                                  taskId: newTaskId,
                                  participantUserIds,
                                  individualUserIds: participantUserIds,
                                  selectedTeamIds: [],
                                };
                              });
                            }}
                          />

                          {activeWaiverParticipantId && !formData.taskId && (
                            <div className="mt-2 ml-1 flex items-center gap-1.5 text-xs font-semibold text-[#7E57C2]">
                              <Info size={14} />
                              Task search is restricted to {activeParticipantName}.
                            </div>
                          )}
                        </div>

            </>
          )}

          <div className="calendar-notes md:col-span-2 relative">
            <CustomInput
              icon={Info}
              name="description"
              type="textarea"
              label={planningMode ? "Plan for this block" : "Internal Notes"}
              placeholder={planningMode ? "What do you plan to work on?" : "Agenda or details..."}
              value={formData.description}
              onChange={(e) => {
                if (e.target.value.length <= MAX_NOTES_LENGTH) {
                  setFormData({ ...formData, description: e.target.value });
                }
              }}
              borderBotttom
            />
            <div className={`absolute bottom-2 right-4 text-[10px] font-bold ${formData.description.length >= MAX_NOTES_LENGTH ? 'text-red-500' : 'text-gray-400'}`}>
              {formData.description.length} / {MAX_NOTES_LENGTH}
            </div>
          </div>
        </div>

        <div className="flex gap-4 pt-6 border-t border-gray-100 text-sm">
          <button type="button" onClick={onClose} className="flex-1 px-8 py-3.5 border border-gray-200 text-gray-600 font-bold rounded-xl hover:bg-gray-50 hover:text-gray-900 transition-all duration-200">
            Cancel
          </button>
          <button type="submit" disabled={loading || !!errors.time} className="flex-1 flex justify-center items-center px-8 py-3.5 bg-[#7E57C2] text-white font-bold rounded-xl hover:bg-[#6a4aa8] shadow-lg shadow-purple-500/20 gap-2 disabled:opacity-50 transition-all duration-200 active:scale-[0.98]">
            {loading ? <div>Scheduling...</div> : <div>{planningMode ? "Schedule Time" : "Schedule"}</div>}
            {!loading && <div className="flex items-center align-middle justify-center"><CheckCircle2 size={16} /></div>}
          </button>
        </div>
      </form>
    </div>
  );
}
